import * as React from 'react';
import {
    OutlinedInput,
    MenuItem,
    FormControl,
    Select,
    ListSubheader,
    TextField,
    Box,
    Typography,
    Tooltip,
    Avatar,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 300,
        },
    },
    disableAutoFocusItem: true,
};

const getInitials = (fullName) => {
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function TruncatedTooltip({ children }) {
    const textRef = React.useRef(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useEffect(() => {
        const el = textRef.current;
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [children]);

    return isTruncated ? (
        <Tooltip title={children}>
            <Typography
                ref={textRef}
                noWrap
                sx={{
                    maxWidth: '160px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    color: '#111',
                    cursor: 'default',
                }}
            >
                {children}
            </Typography>
        </Tooltip>
    ) : (
        <Typography
            ref={textRef}
            noWrap
            sx={{
                maxWidth: '160px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                color: '#111',
            }}
        >
            {children}
        </Typography>
    );
}

export default function SingleMember({
    placeholder = 'Select User',
    options = [],
    selectedValue = '',
    onChange = () => { },
}) {
    const [searchTerm, setSearchTerm] = React.useState('');

    const handleChange = (event) => {
        onChange(event.target.value);
    };

    const filteredOptions = options.filter((opt) =>
        opt.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedOption = options.find((opt) => opt.name === selectedValue);

    return (
        <FormControl
            sx={{
                width: '100%',
                marginBottom: '15px'
            }}
        >
            <Select
                MenuProps={MenuProps}
                value={selectedValue}
                onChange={handleChange}
                displayEmpty
                IconComponent={KeyboardArrowDown}
                input={
                    <OutlinedInput
                        sx={{
                            height: '40px',
                            borderRadius: '8px',
                            paddingRight: '32px',
                            backgroundColor: 'transparent !important', // remove white background
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#CBD5E1',

                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#94A3B8',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#2563EB',
                                borderWidth: '1.5px',
                            },
                            '& .MuiOutlinedInput-root': {
                                height: '40px !important'
                            }
                        }}
                    />
                }
                renderValue={(selected) => {
                    if (!selected) {
                        return (
                            <Typography sx={{ color: '#9CA3AF', fontSize: '14px' }}>
                                {placeholder}
                            </Typography>
                        );
                    }

                    return selectedOption ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {selectedOption.image ? (
                                <Avatar
                                    src={selectedOption.image}
                                    alt={selectedOption.name}
                                    sx={{ width: 28, height: 28 }}
                                />
                            ) : (
                                <Avatar
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        backgroundColor: '#1976d2',
                                        fontSize: 12,
                                        fontWeight: 600,
                                    }}
                                >
                                    {getInitials(selectedOption.name)}
                                </Avatar>
                            )}
                            <Typography sx={{
                                fontSize: '14px',
                                color: '#64748B',
                            }}>
                                {selectedOption.name}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ color: '#9CA3AF', fontSize: '14px' }}>
                            {placeholder}
                        </Typography>
                    );
                }}
                sx={{
                    borderRadius: '8px',
                    width: '100%',
                    color: '#111',
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        height: '40px',
                        fontSize: '14px',
                        fontFamily: 'Manrope',
                    },
                    '& .MuiSelect-icon': {
                        color: '#64748B',
                        right: '8px',
                    },
                    height: '40px'
                }}
            >
                {/* Search box inside dropdown */}
                <ListSubheader>
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 999,
                            background: '#fff',
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize: '13px',
                                },
                            }}
                        />
                    </Box>
                </ListSubheader>

                {/* Options */}
                {filteredOptions.map((opt) => (
                    <MenuItem key={opt.name} value={opt.name}>
                        {opt.image ? (
                            <Avatar
                                src={opt.image}
                                alt={opt.name}
                                sx={{ width: 30, height: 30, mr: 1 }}
                            />
                        ) : (
                            <Avatar
                                sx={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: '#1976d2',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    mr: 1,
                                }}
                            >
                                {getInitials(opt.name)}
                            </Avatar>
                        )}
                        <TruncatedTooltip>{opt.name}</TruncatedTooltip>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
